import { z } from 'zod';
import { Platform } from '@hangar/shared';
import { str, nstr, nint } from './schemaPrimitives';
import { AppSummary, EnvironmentSummary, GitRef, ProfileSummary } from './schemasCommon';

/* ---------- Shadow mode (P3, SPEC §34.1) ---------- */
/*
 * Legacy pipeline runs compared with Hangar's dry-run plan, and the per-app summary behind the cutover rule. Env
 * values are never modelled: `observed` carries key names only and zod strips any other member, so not even a
 * misbehaving API could put a value on screen. Difference and note kinds stay strings (a new kind renders with its code).
 */
export const ShadowDifference = z.object({ kind: str, key: nstr, expected: z.union([str, z.number()]).nullish(), actual: z.union([str, z.number()]).nullish(), message: str });
export type ShadowDifference = z.infer<typeof ShadowDifference>;
export const ShadowNote = z.object({ kind: str, key: nstr, message: str });
export type ShadowNote = z.infer<typeof ShadowNote>;

const platformList = z
  .array(str)
  .default([])
  .transform((list) => list.filter((p): p is 'android' | 'ios' => p === 'android' || p === 'ios'));

export const ShadowRun = z.object({
  id: str,
  app: AppSummary,
  projectId: str.optional(),
  platform: Platform,
  environment: EnvironmentSummary.nullish(),
  profile: ProfileSummary.nullish(),
  /** `nightly` = a provider build the nightly job found without a pipeline report (`shadow_missing`). */
  source: z.enum(['pipeline', 'nightly']).catch('pipeline'),
  /** UTC day of the provider build, `YYYY-MM-DD` (a date-only value: formatted from the string, never through Date). */
  day: str,
  /** An unknown status never reads as a match. */
  status: z.enum(['match', 'differs']).catch('differs'),
  ciContext: z.object({ provider: nstr, workspace: nstr, repository: nstr, pipelineUuid: nstr, stepUuid: nstr, buildNumber: nint, url: nstr, actor: nstr }).nullish(),
  git: GitRef.nullish(),
  observed: z.object({
    providerBuildId: nstr,
    providerBuildUrl: nstr,
    providerProfile: nstr,
    track: nstr,
    versionName: nstr,
    buildNumber: nint,
    envKeys: z.array(str).default([]),
    unreadableKeys: z.array(str).default([]),
    envSources: z.array(str).default([]),
    // `env` (the values the pipeline used) is deliberately absent: the API drops it after the HMAC comparison.
  }),
  plan: z.object({ providerProfile: nstr, track: nstr, versionName: nstr, buildNumber: nint, envContentHash: nstr, blockingChecks: z.array(str).default([]) }).nullish(),
  differences: z.array(ShadowDifference).default([]),
  notes: z.array(ShadowNote).default([]),
  comparedKeys: z.number().default(0),
  /** The console deep link of the app's shadow card (`/projects/<projectSlug>?app=<appSlug>#shadow`). */
  url: nstr,
  createdAt: str,
});
export type ShadowRun = z.infer<typeof ShadowRun>;
export const ShadowRunList = z.object({ data: z.array(ShadowRun), nextCursor: z.string().nullable() });

export const ShadowDay = z.object({ day: str, runs: z.number().default(0), differing: z.number().default(0), missing: z.number().default(0) });
export type ShadowDay = z.infer<typeof ShadowDay>;

export const ShadowSummary = z.object({
  app: AppSummary,
  enabled: z.boolean(),
  since: nstr,
  platforms: platformList,
  requiredDays: z.number(),
  minRuns: z.number(),
  ignoreKeys: z.array(str).default([]),
  streakDays: z.number().default(0),
  windowRuns: z.number().default(0),
  cutoverReady: z.boolean().default(false),
  cutoverReadyAt: nstr,
  lastReport: ShadowDay.nullish(),
  kinds: z.record(str, z.number()).default({}),
  /** Last 30 days, newest first. */
  days: z.array(ShadowDay).default([]),
  /** The app's lockVersion: `If-Match: W/"<version>"` on PATCH. */
  version: z.number(),
});
export type ShadowSummary = z.infer<typeof ShadowSummary>;
